import { useEffect } from 'react';

const VAPI_SCRIPT_URL =
  'https://unpkg.com/@vapi-ai/client-sdk-react/dist/embed/widget.umd.js';

const VAPI_ATTRS: Record<string, string> = {
  'public-key': 'd082cf78-ab0d-419a-bf41-4517b6422f45',
  'assistant-id': '250a62bc-01ed-4fb9-8bc5-5fa23b9cfd08',
  mode: 'chat',
  theme: 'dark',
  'base-bg-color': '#000000',
  'accent-color': '#65acfd',
  'cta-button-color': '#000000',
  'cta-button-text-color': '#ffffff',
  'border-radius': 'large',
  size: 'full',
  position: 'bottom-right',
  title: 'Talk With Us',
  'start-button-text': 'Start',
  'end-button-text': 'End Call',
  'chat-first-message': 'Hey, How can I help you today?',
  'chat-placeholder': 'Type your message...',
  'voice-show-transcript': 'true',
  'consent-required': 'true',
  'consent-title': 'Terms and conditions',
  'consent-content':
    'By clicking "Agree," and each time I interact with this AI agent, I consent to the recording, storage, and sharing of my communications with third-party service providers, and as otherwise described in our Terms of Service.',
  'consent-storage-key': 'vapi_widget_consent',
};

/**
 * Injects the Vapi AI widget after first paint, skipping the work entirely
 * during react-snap prerender. Keeps the third-party script off the critical
 * rendering path and out of prerendered HTML.
 */
const VapiLoader = ({ disabled = false }: { disabled?: boolean }) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (disabled) {
      document.querySelector('vapi-widget')?.remove();
      return;
    }
    let disposed = false;
    if (navigator.userAgent === 'ReactSnap') return;
    if (document.querySelector('vapi-widget')) return;

    const inject = () => {
      if (disposed) return;
      const widget = document.createElement('vapi-widget');
      for (const [k, v] of Object.entries(VAPI_ATTRS)) {
        widget.setAttribute(k, v);
      }
      document.body.appendChild(widget);

      if (!document.querySelector(`script[src="${VAPI_SCRIPT_URL}"]`)) {
        const script = document.createElement('script');
        script.src = VAPI_SCRIPT_URL;
        script.async = true;
        script.onerror = () => {
          console.warn(
            'Vapi widget failed to load — chat functionality may be limited'
          );
        };
        document.body.appendChild(script);
      }
    };

    // Defer injection until the first of: browser idle, a user interaction,
    // or a timeout fallback — whichever fires first — to keep the widget off
    // the critical rendering path.
    let injected = false;
    const interactionEvents = [
      'pointerdown',
      'keydown',
      'touchstart',
      'scroll',
    ] as const;

    const cleanupListeners = () => {
      interactionEvents.forEach(evt => {
        window.removeEventListener(evt, deferredInject);
      });
    };

    function deferredInject() {
      if (injected) return;
      injected = true;
      cleanupListeners();
      inject();
    }

    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          _cb: () => void,
          _opts?: { timeout: number }
        ) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(deferredInject, { timeout: 3000 });
    } else {
      setTimeout(deferredInject, 1500);
    }

    interactionEvents.forEach(evt => {
      window.addEventListener(evt, deferredInject, {
        once: true,
        passive: true,
      });
    });

    return () => {
      disposed = true;
      cleanupListeners();
      document.querySelector('vapi-widget')?.remove();
    };
  }, [disabled]);

  return null;
};

export default VapiLoader;
